<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-box">
        <CoachingOverviewData
            :coaching="coachingData"
            :calculatedUnits="calculatedUnitsData"/>

        <Menu :coaching="coachingData"
              :currentUser="piniaCurrentUser"/>

        <div class="row text-end">
          <div class="col-12">
            <button type="button" class="btn btn-primary" @click="handleInvitationModalForm">
              Einladungsschreiben hinzufügen
            </button>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Termin</th>
              <th>Startzeit</th>
              <th>Dokument erstellt am</th>
              <th>Coach</th>
              <th>Dokument versendet am:</th>
              <th class="text-center">Aktion</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="invitation in invitationList" :key="invitation.id"
                :class="['table-row']"
                @click="handleEditInvitationModalForm(invitation.id)">
              <th>{{ invitation.termin }}</th>
              <th>{{ invitation.startTime }}</th>
              <th>{{ invitation.createdAt }}</th>
              <th>{{ invitation.coach.fullName }}</th>
              <th>{{ invitation.dateWhenItWasSentOut }}</th>
              <td class="center" style="width: 150px!important;">
                <button class="btn btn-primary me-2">
                  PDF
                </button>
                <button class="btn btn-danger ms-2" @click.stop="handleDeleteModal(invitation?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Modal
          v-model="showInvitationModalForm"
          title="Einladungsschreiben"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-lg"
          :hide-footer="true"
          classFooter="justify-content-center">
        <Form :coachingId="coachingId"
              :invitationId="invitationId"
              :coachingStartDate="coachingData.startDate"
              :coachingEndDate="coachingData.endDate"
              v-model:submitForm="submitForm"/>
      </Modal>

      <ModalDelete
          v-model="showDeleteModal"
          :header="t('general.delete_item_header')"
          :onDelete="deleteInvitation">
      </ModalDelete>
    </div>
  </PageTemplate>
</template>

<script setup>
import portalAssistedLivingCoachingCoachingService from '@/api/Portal/AssistedLivingCoaching';
import assistedLivingCoachingService from '@/api/Unemployed/AssistedLivingCoaching';
import assistedLivingCoachingCoachingService from '@/api/Unemployed/AssistedLivingCoaching';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import { piniaUserStore } from '@/store/piniaUserStore';
import CoachingOverviewData from '@/views/App/Portal/AssistedLivingCoaching/CoachingOverviewData.vue';
import Form from '@/views/App/Portal/AssistedLivingCoaching/InvitationToFirstAppointment/Form.vue';
import Menu from '@/views/App/Portal/AssistedLivingCoaching/Menu.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const piniaUserState          = piniaUserStore();
const piniaCurrentUser        = computed(() => piniaUserState.currentUser.user);
const { t }                   = useI18n();
const route                   = useRoute();
const coachingData            = ref('');
const invitationId            = ref('');
const calculatedUnitsData     = ref();
const invitationList          = ref([]);
const coachingId              = +route.params.id;
const isLoaded                = ref(false);
const showInvitationModalForm = ref(false);
const showDeleteModal         = ref(false);
const submitForm              = ref(false);

watch(submitForm, () => {
  showInvitationModalForm.value = false;
  showDeleteModal.value         = false;
});

async function getInvitationsList() {
  try {
    const { data }       = await portalAssistedLivingCoachingCoachingService.getInvitationsList(coachingId);
    invitationList.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getCoachingData() {
  try {
    CloseToast();
    const { data }     = await assistedLivingCoachingCoachingService.getAssistedLivingCoaching(+coachingId);
    coachingData.value = data.data;

    const { data: calculatedUnits } = await assistedLivingCoachingService.getCalculatedUnits(+coachingId);
    calculatedUnitsData.value       = calculatedUnits.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

watch(submitForm, () => {
  invitationId.value            = null;
  showInvitationModalForm.value = false;
  showDeleteModal.value         = false;
  getCoachingData();
  getInvitationsList();
});

function handleInvitationModalForm() {
  CloseToast();
  invitationId.value            = '';
  showInvitationModalForm.value = true;
  showDeleteModal.value         = false;
}

function handleEditInvitationModalForm(id) {
  CloseToast();
  invitationId.value            = id;
  showInvitationModalForm.value = true;
  showDeleteModal.value         = false;
}

function handleDeleteModal(id) {
  CloseToast();
  invitationId.value            = id;
  showDeleteModal.value         = true;
  showInvitationModalForm.value = false;
}

async function deleteInvitation() {
  try {
    await portalAssistedLivingCoachingCoachingService.invitationDelete(invitationId.value);

    invitationId.value    = null;
    showDeleteModal.value = false;

    await getCoachingData();
    await getInvitationsList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getCoachingData(),
    getInvitationsList(),
  ]);
  isLoaded.value = true;
});
</script>

<style scoped>

</style>